// A pane's resize handle (UAT F-146): the window-splitter pattern on a pane's edge. It is a focusable separator named
// by its pane (aria-label), pointing at it (aria-controls), with the pane's width as its value (aria-valuenow between
// aria-valuemin and aria-valuemax); a pointer drags it (captured, so the drag survives leaving the 8 px strip) and the
// keys move it as the caller rules (shell/layout.ts: navKey, asideKey). The strip draws a 4 px rule: clear at rest,
// --border-strong on hover and while dragged, and the design system's focus ring when focused from the keyboard.

import type { KeyboardEvent, PointerEvent } from "react";
import { useState } from "react";
import { cx, useHs } from "@/mc/lib";

export interface ResizeHandleProps {
  /** The pane's name. */
  label: string;
  /** The pane's id. */
  controls: string;
  /** The pane's drawn width in px, and the least and most it may take. */
  value: number;
  min: number;
  max: number;
  /** The edge of its pane the handle sits on: the navigation's end, the assistant's start. */
  edge: "end" | "start";
  /** A pointer drag reached clientX. */
  onDrag: (clientX: number) => void;
  /** A key on the focused handle; true when it was handled (its default is then prevented). */
  onKey: (key: string) => boolean;
  /** A drag starts (true) or ends (false): the frame holds selection and motion off meanwhile. */
  onDragChange?: ((dragging: boolean) => void) | undefined;
  hs?: string | undefined;
}

export function ResizeHandle({ label, controls, value, min, max, edge, onDrag, onKey, onDragChange, hs }: ResizeHandleProps) {
  const hot = useHs(hs);
  const [dragging, setDragging] = useState(false);
  const stop = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setDragging(false);
    onDragChange?.(false);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-controls={controls}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      className={cx("mc-resize", `mc-resize-${edge}`, dragging && "mc-on")}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDragging(true);
        onDragChange?.(true);
      }}
      onPointerMove={(event) => { if (dragging) onDrag(event.clientX); }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => { if (onKey(event.key)) event.preventDefault(); }}
      {...hot}
    />
  );
}
